import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse, Grid, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '../common/Button';
import Card from '../common/Card';
import { SectionHeading } from '../common/styled/SectionHeading';
import { MetaOption } from '../../types';
import { useTranslation } from 'react-i18next';

interface RobsInfo {
  vessel: string;
  manager: string;
  date: string;
  type: string;
  remarks: string;
}

interface RobsInformationCardProps {
  robsInfo: RobsInfo;
  onBack: () => void;
  vessels?: MetaOption[];
  managers?: MetaOption[];
}

const RobsInformationCard: React.FC<RobsInformationCardProps> = ({
  robsInfo,
  onBack,
  vessels,
  managers,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  // Map type values to display labels
  const getTypeLabel = (type: string): string => {
    const typeMap: Record<string, string> = {
      standard: t('robs.standardRob'),
      STD: t('robs.standardRob'),
      endOfManagement: t('robs.endOfManagementRob'),
      EOM: t('robs.endOfManagementRob'),
      newBuilding: t('robs.newBuildingRob'),
      NEW: t('robs.newBuildingRob'),
      secondHand: t('robs.secondHandRob'),
      ACQ: t('robs.secondHandRob'),
    };
    return typeMap[type] || type;
  };

  return (
    <Card
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          mb: expanded ? 1.5 : 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          width: '100%',
          transition: `margin-bottom ${theme.transitions.custom.slow}`,
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <SectionHeading>{t('robs.robInformation')}</SectionHeading>

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Button variant="outlinedBlue" onClick={onBack}>
            {t('buttons.edit')}
          </Button>
          <IconButton
            size="small"
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: `transform ${theme.transitions.custom.default}`,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            <ExpandMoreIcon sx={{ color: theme.palette.modal.title }} />
          </IconButton>
        </Box>
      </Box>

      <Collapse
        in={expanded}
        timeout={500}
        easing={{
          enter: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
          exit: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
        }}
        sx={{
          width: '100%',
          '& .MuiCollapse-wrapperInner': {
            willChange: 'height',
          },
        }}
      >
        <div>
          {/* Custom divider that spans full width */}
          <Box
            sx={{
              height: '2px',
              backgroundColor: theme.palette.dropdown.border,
              mb: 3,
              mx: -2,
              width: 'calc(100% + 32px)',
            }}
          />

          <Grid container spacing={1}>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.modal.title,
                  fontSize: '16px',
                  minWidth: 120,
                }}
              >
                {t('form.vessel')}
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.modal.title,
                  fontSize: '15px',
                  fontWeight: 400,
                  mt: 1,
                }}
              >
                {vessels?.find((vessel) => vessel.value === robsInfo.vessel)?.label ||
                  robsInfo.vessel ||
                  ''}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.modal.title,
                  fontSize: '16px',
                  minWidth: 120,
                }}
              >
                {t('form.manager')}
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.modal.title,
                  fontSize: '15px',
                  fontWeight: 400,
                  mt: 1,
                }}
              >
                {managers?.find((manager) => manager.value === robsInfo.manager)?.label ||
                  robsInfo.manager ||
                  '-'}
              </Typography>
            </Grid>

            {robsInfo?.type?.toLowerCase() === 'standard' && (
              <Grid size={{ xs: 12, md: 2.4 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.modal.title,
                    fontSize: '16px',
                    minWidth: 120,
                  }}
                >
                  {t('form.date')}
                </Typography>
                <Typography
                  sx={{
                    color: theme.palette.modal.title,
                    fontSize: '15px',
                    fontWeight: 400,
                    mt: 1,
                  }}
                >
                  {robsInfo.date || '-'}
                </Typography>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 2.4 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.modal.title,
                  fontSize: '16px',
                  minWidth: 120,
                }}
              >
                {t('form.type')}
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.modal.title,
                  fontSize: '15px',
                  fontWeight: 400,
                  mt: 1,
                }}
              >
                {getTypeLabel(robsInfo.type) || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: theme.palette.modal.title,
                  fontSize: '16px',
                  minWidth: 120,
                }}
              >
                {t('form.remarks')}
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.modal.title,
                  fontSize: '15px',
                  fontWeight: 400,
                  mt: 1,
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {robsInfo.remarks || '-'}
              </Typography>
            </Grid>
          </Grid>
        </div>
      </Collapse>
    </Card>
  );
};

export default RobsInformationCard;
